import React from "react";
import Checkbox from "material-ui/Checkbox";
import CheckboxIcon from "material-ui/svg-icons/toggle/check-box";
import moment from "moment";
import { format } from "lib/globalize-util";
import { IShipDateActions, IShipDateDictionary, ActionType } from "./date-organizer";
import { getDisplayName } from "../build-order-utils";

interface IDateCheckboxProps {
    shipStart: string;
    shipEnd: string;
    productCount: number;
    shipDateDictionary: IShipDateDictionary;
    shipDateAction: IShipDateActions;
    onSetShipDateDictionary: (shipDateDictionary: IShipDateDictionary) => void;
    onSetShipDateAction: (shipDateAction: IShipDateActions) => void;
    productId: string;
    earliestAvailableDate: number;
    latestAvailableDate: number;
}

export const DateCheckbox: React.FunctionComponent<IDateCheckboxProps> = ({
    shipStart,
    shipEnd,
    productCount,
    shipDateDictionary,
    onSetShipDateDictionary,
    shipDateAction,
    onSetShipDateAction,
    productId,
    earliestAvailableDate,
    latestAvailableDate,
}) => {
    const key = React.useRef<string>(`${shipStart}-${shipEnd}`);
    key.current = `${shipStart}-${shipEnd}`;

    const isChecked = shipDateDictionary[key.current]?.includes(productId);

    const handleOnCheck = React.useCallback(
        (_event: React.MouseEvent<{}>, checked: boolean) => {
            const _shipDateDictionary = { ...shipDateDictionary };
            const _shipDateAction = { ...shipDateAction };
            if (checked) {
                _shipDateDictionary[key.current]
                    ? _shipDateDictionary[key.current].push(productId)
                    : (_shipDateDictionary[key.current] = [productId]);
                _shipDateAction[key.current] = ActionType.Add;
            } else {
                _shipDateDictionary[key.current] = _shipDateDictionary[key.current].filter(
                    (entry) => entry !== productId
                );
                _shipDateAction[key.current] = ActionType.Remove;
            }
            onSetShipDateDictionary(_shipDateDictionary);
            onSetShipDateAction(_shipDateAction);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shipDateDictionary]
    );

    const shouldBeDisabled = React.useMemo(() => {
        let _shouldBeDisabled = false;
        if (moment.utc(shipStart).isBefore(moment.utc(earliestAvailableDate))) {
            _shouldBeDisabled = true;
        } else if (latestAvailableDate && moment.utc(shipEnd).isAfter(moment.utc(latestAvailableDate))) {
            _shouldBeDisabled = true;
        }
        return _shouldBeDisabled;
    }, [earliestAvailableDate, latestAvailableDate, shipStart, shipEnd]);

    return (
        <div>
            <Checkbox
                checkedIcon={<CheckboxIcon />}
                disabled={shouldBeDisabled}
                checked={isChecked}
                onCheck={handleOnCheck}
                label={<DateLabel shipStart={shipStart} shipEnd={shipEnd} productCount={productCount} />}
            />
        </div>
    );
};

export const DateLabel: React.FunctionComponent<{ shipStart: string; shipEnd: string; productCount: number }> = ({
    shipStart,
    shipEnd,
    productCount,
}) => (
    <div>
        {getDisplayName({ ship_start: shipStart, ship_end: shipEnd })}
        <span className="productCount">{`(${format("Products")}: ${productCount || 0})`}</span>
    </div>
);
